import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Sidebar, Avatar, Dropdown, Button } from 'flowbite-react';
import {
  HiChartPie,
  HiClipboardList,
  HiPhotograph,
  HiUsers,
  HiCog,
  HiOfficeBuilding,
  HiMenu,
  HiX,
  HiLogout,
  HiDotsVertical,
  HiInformationCircle,
  HiExclamation,
} from 'react-icons/hi';
import { authStore } from '../stores/authStore';
import ToastContainer from '../components/ToastContainer';

const MainLayout: React.FC = observer(() => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleLogout = async () => {
    await authStore.logout();
    navigate('/login');
  };

  const handleStopImpersonation = () => {
    authStore.stopImpersonation();
    navigate('/tenants');
  };

  // rootテナント判定 - impersonating時は通常テナントとして扱う
  const isRootTenant = authStore.tenantId === 'root' && !authStore.isImpersonating;

  // rootテナント用メニュー
  const rootTenantMenuItems = [
    {
      path: '/tenants',
      label: 'テナント管理',
      icon: HiOfficeBuilding,
      roles: ['root'],
    },
    {
      path: '/users',
      label: 'ユーザー管理',
      icon: HiUsers,
      roles: ['root'],
    },
    {
      path: '/system-settings',
      label: 'システム設定',
      icon: HiCog,
      roles: ['root'],
    },
    {
      path: '/system-info',
      label: 'システム情報',
      icon: HiInformationCircle,
      roles: ['root'],
    },
  ];

  // 通常テナント用メニュー
  const normalTenantMenuItems = [
    {
      path: '/dashboard',
      label: 'ダッシュボード',
      icon: HiChartPie,
      roles: ['admin', 'user'],
    },
    {
      path: '/registrations',
      label: 'ゲスト管理',
      icon: HiClipboardList,
      roles: ['admin', 'user'],
    },
    {
      path: '/screens',
      label: '画面履歴',
      icon: HiPhotograph,
      roles: ['admin'],
    },
    {
      path: '/users',
      label: 'ユーザー管理',
      icon: HiUsers,
      roles: ['admin'],
    },
    {
      path: '/settings',
      label: 'テナント設定',
      icon: HiCog,
      roles: ['admin'],
    },
  ];

  // 使用するメニューアイテムを選択
  const menuItems = isRootTenant ? rootTenantMenuItems : normalTenantMenuItems;

  // なりすまし中のロール判定
  // なりすまし中のrootユーザーはadminとして扱う
  const effectiveRole = authStore.isImpersonating && authStore.user?.role === 'root'
    ? 'admin'
    : (authStore.user?.role || 'user');

  // ロールによるフィルタリング
  const visibleMenuItems = menuItems.filter(item =>
    item.roles.includes(effectiveRole)
  );

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div
        className={`${
          sidebarOpen ? 'w-64' : 'w-0'
        } transition-all duration-300 bg-white shadow-lg`}
      >
        {sidebarOpen && (
          <Sidebar className="h-full flex flex-col">
            <div className='flex flex-col h-full'>
              <div className="flex items-center justify-between p-4 border-b">
                <img src="/logo.png" alt="Med-Well Call" className="h-8" />
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="lg:hidden text-gray-500 hover:text-gray-700"
                >
                  <HiX className="w-6 h-6" />
                </button>
              </div>

              <div className="flex-1 flex flex-col justify-between">
                <Sidebar.Items>
                  <Sidebar.ItemGroup>
                    {visibleMenuItems.map((item) => (
                      <Sidebar.Item
                        key={item.path}
                        as={Link}
                        to={item.path}
                        icon={item.icon}
                        active={location.pathname === item.path}
                      >
                        {item.label}
                      </Sidebar.Item>
                    ))}
                  </Sidebar.ItemGroup>
                </Sidebar.Items>

                {/* User info at bottom */}
                <div className="border-t p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <Avatar
                        rounded
                        size="sm"
                        placeholderInitials={
                          authStore.user?.name?.charAt(0) ||
                          authStore.user?.email?.charAt(0) ||
                          'U'
                        }
                      />
                      <div className="text-left min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {authStore.user?.name || authStore.user?.email}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {authStore.user?.role === 'root' ? 'Root Admin' :
                          authStore.user?.role === 'admin' ? 'Admin' : 'User'}
                        </p>
                      </div>
                    </div>
                    <Dropdown
                      label=""
                      renderTrigger={() => (
                        <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0">
                          <HiDotsVertical className="w-5 h-5 text-gray-400" />
                        </button>
                      )}
                      arrowIcon={false}
                      inline
                    >
                    <Dropdown.Header>
                      <span className="block text-sm">{authStore.user?.name}</span>
                      <span className="block text-xs text-gray-500">{authStore.user?.email}</span>
                    </Dropdown.Header>
                    {/* <Dropdown.Item icon={HiUser}>
                      プロフィール
                    </Dropdown.Item> */}
                    <Dropdown.Divider />
                    <Dropdown.Item icon={HiLogout} onClick={handleLogout}>
                      ログアウト
                    </Dropdown.Item>
                    </Dropdown>
                  </div>
                </div>
              </div>
            </div>
          </Sidebar>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Impersonation Banner */}
        {authStore.isImpersonating && (
          <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-3">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center">
                <HiExclamation className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0" />
                <span className="text-yellow-800">
                  現在 <strong>{authStore.impersonatingTenantName}</strong> のテナント管理者として管理画面を操作しています
                </span>
              </div>
              <Button
                size="xs"
                color="warning"
                onClick={handleStopImpersonation}
              >
                なりすましを終了
              </Button>
            </div>
          </div>
        )}

        {/* Mobile menu button */}
        {!sidebarOpen && (
          <div className="bg-white shadow-sm border-b px-4 py-3 lg:hidden">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-gray-500 hover:text-gray-700"
            >
              <HiMenu className="w-6 h-6" />
            </button>
          </div>
        )}

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>

      {/* Toast Container */}
      <ToastContainer />
    </div>
  );
});

export default MainLayout;